import { useEffect, useSyncExternalStore } from 'react';

import { useAuth } from '@opendoor/auth-fe/context';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import { NavPurchaseOffersDocument } from 'components/buyer/__generated__/purchase-offers.zeus';
import {
  buildSaleHref,
  isActiveSaleTransaction,
} from 'components/buyer/hub/saleTransactionCardModel';
import { isActiveBuyerOffer } from 'helpers/buyerOfferStatus';
import { captureError } from 'helpers/captureError';

import type { NavSellerTransactionsQuery } from './__generated__/nav-seller-transactions.zeus';
import { NavSellerTransactionsDocument } from './__generated__/nav-seller-transactions.zeus';

interface NavTransactionBase {
  id: string;
  /** Street line shown in the account nav. */
  street: string;
  href: string;
}

export type NavTransaction = NavTransactionBase &
  (
    | { type: 'purchase' }
    | {
        type: 'sale';
        /** Status suffix appended for inactive seller offers, e.g. "Past offer"/"Withdrawn". */
        statusLabel?: string;
      }
  );

export type NavTransactionType = NavTransaction['type'];

export const navTransactionLabel = (transaction: NavTransaction): string =>
  transaction.type === 'sale' && transaction.statusLabel
    ? `${transaction.street} (${transaction.statusLabel})`
    : transaction.street;

interface NavStoreState {
  customerUuid: string | null;
  transactions: NavTransaction[];
}

interface NavLoad {
  customerUuid: string;
  controller: AbortController;
  promise: Promise<void>;
}

type NavSellerTransaction =
  NavSellerTransactionsQuery['customerAccount']['seller']['transactions'][number];

const EMPTY_NAV_TRANSACTIONS: NavTransaction[] = [];
const EMPTY_NAV_STORE_STATE: NavStoreState = {
  customerUuid: null,
  transactions: EMPTY_NAV_TRANSACTIONS,
};

// Desktop, mobile, and the header prefetch share one customer-scoped request.
let storeState: NavStoreState = EMPTY_NAV_STORE_STATE;
const listeners = new Set<() => void>();
let requestId = 0;
let activeLoad: NavLoad | null = null;

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = (): NavStoreState => storeState;
const getServerSnapshot = (): NavStoreState => EMPTY_NAV_STORE_STATE;

const setStoreState = (next: NavStoreState) => {
  storeState = next;
  listeners.forEach((listener) => listener());
};

const mapSellerTransactions = (transactions: NavSellerTransaction[]): NavTransaction[] =>
  transactions
    .map((transaction): NavTransaction | null => {
      const base = {
        id: transaction.id,
        type: 'sale',
        street: transaction.address.street || 'Your sale',
        href: buildSaleHref(transaction),
      } satisfies NavTransaction;

      if (isActiveSaleTransaction(transaction)) {
        return base;
      }
      if (transaction.__typename === 'DeniedTransaction') {
        return { ...base, statusLabel: 'Past offer' };
      }
      if (transaction.__typename === 'WithdrawnTransaction') {
        return { ...base, statusLabel: 'Withdrawn' };
      }
      return null;
    })
    .filter((transaction): transaction is NavTransaction => transaction !== null);

const requestNavTransactions = async (
  customerUuid: string,
  id: number,
  signal: AbortSignal,
): Promise<boolean> => {
  const [salesResult, purchaseResult] = await Promise.allSettled([
    Promise.resolve().then(() => requestZeusFromBrowser(NavSellerTransactionsDocument, {}, signal)),
    Promise.resolve().then(() => requestZeusFromBrowser(NavPurchaseOffersDocument, {}, signal)),
  ]);
  if (id !== requestId || storeState.customerUuid !== customerUuid) {
    return true;
  }

  if (salesResult.status === 'rejected') {
    captureError(salesResult.reason, 'NavSellerTransactions');
  }
  if (purchaseResult.status === 'rejected') {
    captureError(purchaseResult.reason, 'NavPurchaseOffers');
  }
  if (salesResult.status === 'rejected' && purchaseResult.status === 'rejected') {
    return false;
  }

  const currentTransactions = storeState.transactions;
  const purchases: NavTransaction[] =
    purchaseResult.status === 'fulfilled'
      ? purchaseResult.value.customerAccount.buyer.offers
          .filter((offer) => isActiveBuyerOffer(offer.transactionState))
          .map((offer) => ({
            id: offer.id,
            type: 'purchase',
            street: offer.listing?.marketplaceListing?.street ?? 'Your purchase',
            href: `/dashboard/buy/${offer.id}`,
          }))
      : currentTransactions.filter((transaction) => transaction.type === 'purchase');

  const sales: NavTransaction[] =
    salesResult.status === 'fulfilled'
      ? mapSellerTransactions(salesResult.value.customerAccount.seller.transactions)
      : currentTransactions.filter((transaction) => transaction.type === 'sale');

  setStoreState({ customerUuid, transactions: [...purchases, ...sales] });
  return salesResult.status === 'fulfilled' && purchaseResult.status === 'fulfilled';
};

const fetchNavTransactions = (customerUuid: string): Promise<void> => {
  if (activeLoad?.customerUuid === customerUuid) {
    return activeLoad.promise;
  }

  activeLoad?.controller.abort();
  const controller = new AbortController();
  const id = ++requestId;
  if (storeState.customerUuid !== customerUuid) {
    setStoreState({ customerUuid, transactions: EMPTY_NAV_TRANSACTIONS });
  }

  const promise = requestNavTransactions(customerUuid, id, controller.signal)
    .then((succeeded) => {
      if (!succeeded && activeLoad?.promise === promise) {
        activeLoad = null;
      }
      return undefined;
    })
    .catch((error) => {
      if (id === requestId) {
        captureError(error, 'NavTransactions');
      }
      if (activeLoad?.promise === promise) {
        activeLoad = null;
      }
    });
  activeLoad = { customerUuid, controller, promise };
  return promise;
};

const clearNavTransactions = () => {
  requestId += 1;
  activeLoad?.controller.abort();
  activeLoad = null;
  if (storeState !== EMPTY_NAV_STORE_STATE) {
    setStoreState(EMPTY_NAV_STORE_STATE);
  }
};

export const resetNavTransactionsStoreForTest = () => {
  requestId += 1;
  activeLoad?.controller.abort();
  activeLoad = null;
  storeState = EMPTY_NAV_STORE_STATE;
};

interface NavAuthentication {
  state: ReturnType<typeof useAuth>['authentication']['state'];
  customerUuid: string | null;
}

const useNavTransactionsSync = (): NavAuthentication => {
  const { authentication } = useAuth();
  const authState = authentication.state;
  const customerUuid = authState === 'authenticated' ? authentication.user.customerUuid : null;

  useEffect(() => {
    if (customerUuid !== null) {
      void fetchNavTransactions(customerUuid);
      return;
    }
    if (authState === 'authenticating') {
      return;
    }
    clearNavTransactions();
  }, [customerUuid, authState]);

  return { state: authState, customerUuid };
};

/**
 * Warms the shared nav-transactions store without subscribing to it. Mount this
 * in an always-rendered header slot (e.g. `AccountSection`) so the request is in
 * flight before any menu opens — important for the mobile menu, whose list only
 * mounts on open.
 */
export const usePrefetchNavTransactions = (): void => {
  useNavTransactionsSync();
};

/**
 * Active purchase offers and sale transactions for the account navigation,
 * prefetched on auth and shared across every header surface.
 */
export const useNavTransactions = (): NavTransaction[] => {
  const authentication = useNavTransactionsSync();
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  if (snapshot.customerUuid !== authentication.customerUuid) {
    return EMPTY_NAV_TRANSACTIONS;
  }
  return snapshot.transactions;
};
